import { FUNC_NOOP } from './constants';
import { Subscription } from './subscription';

const DEFAULT_PING_INTERVAL = 5 * 1000;
const DEFAULT_RECONNECT_INTERVALS = [1 * 1000, 3 * 1000, 5 * 1000, 10 * 1000];
const DEFAULT_PING_MESSAGE = 'null';

export type ISocketEvents<T> =
  | { $: 'reconnecting' }
  | { $: 'reconnected' }
  | { $: 'connected' }
  | { $: 'closed'; code: number; reason: string }
  | { $: 'error'; event: Event }
  | ({ $?: undefined } & T);

export interface ISocketClientOptions {
  pingInterval?: number;
  pingMessage?: string;
  reconnectIntervals?: number[];
  shouldReconnect?: (code: number, reason: string) => boolean;
}

export type ISocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'reconnected' | 'closed';

export class SocketClient<T = object> {
  constructor(url: string | (() => string), options?: ISocketClientOptions) {
    this.#url = typeof url === 'function' ? url : () => url;
    this.#pingInterval = options?.pingInterval ?? DEFAULT_PING_INTERVAL;
    this.#pingMessage = options?.pingMessage ?? DEFAULT_PING_MESSAGE;
    this.#reconnectIntervals = options?.reconnectIntervals ?? DEFAULT_RECONNECT_INTERVALS;
    this.#shouldReconnect = options?.shouldReconnect;
    this.#createSocket();
  }

  #url: () => string;
  #ws: WebSocket | null = null;
  #subscription = new Subscription<ISocketEvents<T>>();

  #wasConnected = false;
  #isReconnected = false;

  #pingInterval: number;
  #pingMessage: string;
  #pingTimeout: ReturnType<typeof setTimeout> | null = null;

  #reconnectAttempts = 0;
  #reconnectIntervals: number[];
  #reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  #shouldReconnect: ((code: number, reason: string) => boolean) | undefined;

  #discarded = false;

  get connected() {
    return this.#ws?.readyState === WebSocket.OPEN;
  }

  get status(): ISocketStatus {
    if (this.#ws?.readyState === WebSocket.OPEN) {
      return this.#isReconnected ? 'reconnected' : 'connected';
    }

    if (this.#reconnectTimeout) {
      return 'reconnecting';
    }

    if (this.#ws) {
      return this.#wasConnected ? 'reconnecting' : 'connecting';
    }

    return 'closed';
  }

  get discarded() {
    return this.#discarded;
  }

  subscribe(cb: (e: ISocketEvents<T>) => void): () => void {
    if (this.#discarded) return FUNC_NOOP;

    return this.#subscription.subscribe(cb);
  }

  send(data: string): boolean {
    if (this.#ws?.readyState === WebSocket.OPEN) {
      this.#ws.send(data);
      return true;
    }

    return false;
  }

  discard() {
    this.#discarded = true;
    this.#cleanupSocket();
    this.#subscription.clear();
  }

  #createSocket() {
    if (this.#discarded) return;

    const ws = new WebSocket(this.#url());

    this.#reconnectAttempts++;

    ws.onmessage = (e) => {
      this.#subscription.publish(JSON.parse(e.data));
    };

    ws.onclose = (e) => {
      this.#cleanupSocket();
      this.#subscription.publish({
        $: 'closed',
        code: e.code,
        reason: e.reason,
      });
      const shouldReconnect = this.#shouldReconnect
        ? this.#shouldReconnect(e.code, e.reason)
        : e.code !== 1000;
      if (shouldReconnect) {
        this.#reconnect();
      }
    };

    ws.onerror = (e) => {
      this.#cleanupSocket();
      this.#subscription.publish({ $: 'error', event: e });
      this.#reconnect();
    };

    ws.onopen = () => {
      const isReconnect = this.#wasConnected;
      this.#wasConnected = true;
      this.#isReconnected = isReconnect;
      this.#reconnectAttempts = 0;
      this.#schedulePing();
      this.#subscription.publish(isReconnect ? { $: 'reconnected' } : { $: 'connected' });
    };

    this.#ws = ws;
  }

  #cleanupSocket() {
    if (this.#ws) {
      this.#ws.onclose = this.#ws.onerror = this.#ws.onmessage = this.#ws.onopen = null;
      this.#ws.close();
      this.#ws = null;
    }

    if (this.#pingTimeout) {
      clearTimeout(this.#pingTimeout);
      this.#pingTimeout = null;
    }

    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = null;
    }
  }

  #schedulePing() {
    this.#pingTimeout = setTimeout(() => {
      if (!this.#ws) return;

      this.#ws.send(this.#pingMessage);
      this.#schedulePing();
    }, this.#pingInterval);
  }

  #reconnect() {
    if (this.#discarded) return;

    this.#subscription.publish({ $: 'reconnecting' });

    if (!this.#reconnectAttempts) {
      this.#createSocket();
    } else {
      const time =
        this.#reconnectIntervals[this.#reconnectAttempts - 1] ||
        this.#reconnectIntervals[this.#reconnectIntervals.length - 1];
      this.#reconnectTimeout = setTimeout(() => {
        this.#reconnectTimeout = null;
        this.#createSocket();
      }, time);
    }
  }
}
